import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toastService } from '../../../../services/toast';
import { coursesApi } from '../../../../services/api/courses.api';
import BasicInfo from './components/BasicInfo';
import CourseOptions from './components/CourseOptions';
import Scheduling from './components/Scheduling';
import Instructors from './components/Instructors';
import StepperHeader from './components/StepperHeader';

const CreateCourse = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [instructorsList, setInstructorsList] = useState([]);
  const [courseData, setCourseData] = useState({
    // Basic Info
    name: '',
    description: '',
    coverImage: null,
    coverImagePreview: null,
    planImage: null,
    planImagePreview: null,
    
    whatYouWillLearn: '',
    // Course Options
    courseType: 'free',
    price: '0',
    duration: '',
    category: '',
    status: 'draft',
    askInstructor: 'enabled',
    sections: [], // أقسام الدورة
    
    // Discount Data
    hasDiscount: false,
    discountType: 'percentage',
    discountValue: '',
    discountStart: '',
    discountEnd: '',
    discount_start_date: '',
    discount_end_date: '',

    // Scheduling
    startDate: '',
    endDate: '',
    schedule: [],
    
    // Instructors
    instructors: []
  });

  // جلب التصنيفات والمدربين عند تحميل الصفحة
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesData, instructorsData] = await Promise.all([
          coursesApi.getCategories(),
          coursesApi.getInstructors()
        ]);
        
        setCategories(categoriesData);
        setInstructorsList(instructorsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toastService.error('حدث خطأ أثناء تحميل البيانات');
      }
    };
    
    fetchData();
  }, []);

  const updateData = (newData) => {
    setCourseData(prev => ({
      ...prev,
      ...newData
    }));
  };

  const isStepComplete = (stepId) => {
    switch (stepId) {
      case 1: // المعلومات الأساسية
        return courseData.name && courseData.description && courseData.coverImage;
      case 2: // الخيارات
        return courseData.category && courseData.duration;
      case 3: // الجدولة
        return courseData.startDate && courseData.endDate;
      case 4: // المدربون
        return courseData.instructors && courseData.instructors.length > 0;
      default:
        return false;
    }
  };

  const canPublish = () => {
    // التحقق من اكتمال جميع الحقول الإجبارية
    return isStepComplete(1) && isStepComplete(2) && isStepComplete(3) && isStepComplete(4);
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
  };

  const handleSaveDraft = async () => {
    // التحقق من الحقول الإجبارية حسب المرحلة الحالية
    let requiredFields = [];
    let missingFields = [];
    
    // الحقول الإجبارية للمرحلة الأولى
    if (currentStep >= 1) {
      requiredFields = ['name', 'coverImage'];
      if (!courseData.name) missingFields.push('اسم الدورة');
      if (!courseData.coverImage) missingFields.push('غلاف الدورة');
    }
    
    // إذا كانت هناك حقول مفقودة في المرحلة الحالية، نعرض رسالة خطأ
    if (missingFields.length > 0) {
      toastService.error(`يجب إكمال الحقول التالية لحفظ المسودة: ${missingFields.join('، ')}`);
      return;
    }
    
    // إذا تم إدخال اسم الدورة وهناك حقول مطلوبة في الخطوات التالية
    if (courseData.name) {
      // التحقق من الحقول المفقودة في الخطوات التالية
      const nextStepsMissingFields = [];
      
      // الخطوة الثانية
      if (currentStep < 2) {
        if (!courseData.category) nextStepsMissingFields.push('التصنيف');
        if (!courseData.duration) nextStepsMissingFields.push('المدة');
      }
      
      // إذا كانت هناك حقول مفقودة في الخطوات التالية، نعرض تنبيه
      if (nextStepsMissingFields.length > 0 && currentStep === 1) {
        // لا نعرض رسالة هنا لتجنب التكرار مع رسالة الخطأ
      }
    }
    
    setLoading(true);
    try {
      // تعيين حالة الدورة كمسودة
      const dataToSend = {
        ...courseData,
        status: 'draft'
      };
      
      await coursesApi.create(dataToSend);
      
      if (courseData.schedule && courseData.schedule.length > 0) {
        toastService.success('تم حفظ الدورة المجدولة كمسودة بنجاح');
      } else {
        toastService.success('تم حفظ الدورة كمسودة بنجاح');
      }
      navigate('/admin/dashboard/courses');
    } catch (error) {
      console.error('Error saving draft:', error);
      
      // عرض رسائل خطأ محددة بناءً على استجابة الخادم
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        const errorFields = Object.keys(errors);
        
        // إنشاء قائمة بالحقول المفقودة مع ترجمتها للعربية
        const fieldTranslations = {
          'name': 'اسم الدورة',
          'description': 'وصف الدورة',
          'cover_image': 'غلاف الدورة',
          'course_type': 'نوع الدورة',
          'category_id': 'التصنيف',
          'duration': 'المدة',
          'start_date': 'تاريخ البداية',
          'end_date': 'تاريخ النهاية',
          'instructors': 'المدربين'
        };
        
        // تصنيف الحقول حسب الخطوات
        const stepFields = {
          1: ['name', 'description', 'cover_image'],
          2: ['course_type', 'category_id', 'duration'],
          3: ['start_date', 'end_date'],
          4: ['instructors']
        };
        
        // تحديد الحقول المفقودة في الخطوة الحالية
        const currentStepMissingFields = errorFields.filter(field => stepFields[currentStep]?.includes(field));
        
        // تحديد الحقول المفقودة في الخطوات التالية
        const nextStepsMissingFields = errorFields.filter(field => {
          for (let step = currentStep + 1; step <= 4; step++) {
            if (stepFields[step]?.includes(field)) return true;
          }
          return false;
        });
        
        // ترجمة الحقول المفقودة إلى العربية
        const currentStepMissingFieldsArabic = currentStepMissingFields.map(field => fieldTranslations[field] || field);
        const nextStepsMissingFieldsArabic = nextStepsMissingFields.map(field => fieldTranslations[field] || field);
        
        // عرض رسائل الخطأ المناسبة
        if (currentStepMissingFieldsArabic.length > 0) {
          // رسالة خطأ للحقول المفقودة في الخطوة الحالية
          toastService.error(`الحقول المطلوبة في الخطوة الحالية: ${currentStepMissingFieldsArabic.join('، ')}`);
        } else if (nextStepsMissingFieldsArabic.length > 0) {
          // رسالة تنبيه للحقول المطلوبة في الخطوات التالية
          toastService.info(`لحفظ الدورة كمسودة اكمل الخطوة التالية أولا`);
        } else {
          toastService.error('يرجى التحقق من صحة البيانات المدخلة');
        }
      } else {
        toastService.error('حدث خطأ أثناء حفظ المسودة. تأكد من إكمال الحقول المطلوبة.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    // التحقق من اكتمال جميع الحقول قبل النشر
    if (!canPublish()) {
      // تحقق من الحقول المفقودة وعرض رسالة تفصيلية
      const missingFields = [];
      if (!courseData.name) missingFields.push('اسم الدورة');
      if (!courseData.description) missingFields.push('وصف الدورة');
      if (!courseData.coverImage) missingFields.push('صورة الغلاف');
      if (!courseData.category) missingFields.push('التصنيف');
      if (!courseData.duration) missingFields.push('المدة');
      if (!courseData.startDate) missingFields.push('تاريخ البداية');
      if (!courseData.endDate) missingFields.push('تاريخ النهاية');
      if (!courseData.instructors || courseData.instructors.length === 0) missingFields.push('المدربين');
      
      // تصنيف الحقول حسب الخطوات
      const stepFields = {
        1: ['name', 'description', 'coverImage'],
        2: ['category', 'duration'],
        3: ['startDate', 'endDate'],
        4: ['instructors']
      };
      
      // تحديد الخطوة التي تحتاج إكمال
      let incompleteStep = 0;
      for (let step = 1; step <= 4; step++) {
        const stepMissingFields = stepFields[step].filter(field => {
          if (field === 'instructors') return !courseData[field] || courseData[field].length === 0;
          return !courseData[field];
        });
        if (stepMissingFields.length > 0) {
          incompleteStep = step;
          break;
        }
      }
      
      if (incompleteStep > 0) {
        toastService.error(`يرجى إكمال الحقول في الخطوة ${incompleteStep}: ${missingFields.join('، ')}`);
      } else {
        toastService.error(`يرجى إكمال الحقول التالية قبل النشر: ${missingFields.join('، ')}`);
      }
      return;
    }

    // نشر الدورة
    setLoading(true);
    try {
      // تعيين حالة الدورة كمنشورة
      const dataToSend = {
        ...courseData,
        status: 'published'
      };
      
      await coursesApi.create(dataToSend);
      
      if (courseData.schedule && courseData.schedule.length > 0) {
        toastService.success('تم نشر الدورة المجدولة بنجاح');
      } else {
        toastService.success('تم نشر الدورة بنجاح');
      }
      navigate('/admin/dashboard/courses');
    } catch (error) {
      console.error('Error publishing course:', error);
      toastService.error('حدث خطأ أثناء نشر الدورة');
    } finally {
      setLoading(false);
    }
  };

  const handleDataUpdate = (step, newData) => {
    setCourseData(prevData => ({
      ...prevData,
      ...newData
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <BasicInfo data={courseData} updateData={updateData} />;
      case 2:
        return <CourseOptions 
          data={courseData} 
          categories={categories}
          updateData={(newData) => handleDataUpdate('options', newData)} 
        />;
      case 3:
        return <Scheduling 
          data={courseData} 
          updateData={(newData) => handleDataUpdate('schedule', newData)} 
        />;
      case 4:
        return <Instructors 
          data={courseData.instructors}
          availableInstructors={instructorsList}
          updateData={(newInstructors) => handleDataUpdate('instructors', { instructors: newInstructors })} 
        />;
      default:
        return null;
    }
  };

  return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center py-4">إنشاء دورة جديدة</h1>
          </div>
          <div className={`pb-2 pt-8 px-6 ${currentStep === 1 ? '' : 'animate-fadeIn'}`}> 
            <StepperHeader 
              currentStep={currentStep} 
              onStepClick={handleStepClick}
              isStepComplete={isStepComplete}
            />
          </div>
          <div className="flex-grow pt-0 px-6 pb-6 min-h-[400px]">
            {renderStep()}
          </div>
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            <div className="relative flex justify-center items-center">
              <div className="flex gap-4">
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    disabled={loading}
                  >
                    <span>السابق</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
                {currentStep < 4 && (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                    disabled={loading}
                  >
                    <span>التالي</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="absolute left-6 flex gap-4">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h1a2 2 0 012 2v7a2 2 0 01-2 2H7a2 2 0 01-2-2V8a2 2 0 012-2h1v5.586l-1.293-1.293z" />
                  </svg>
                  <span>حفظ كمسودة</span>
                </button>
                <button
                  type="button"
                  onClick={handlePublish}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-green-600 dark:bg-green-500 rounded-md hover:bg-green-700 dark:hover:bg-green-600 active:bg-green-800 dark:active:bg-green-700 shadow-sm hover:shadow transition-all duration-200"
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{loading ? 'جاري الحفظ...' : 'نشر'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default CreateCourse;
